import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CircleCheck, Info, MoreHorizontal, OctagonAlert, Sparkles, TriangleAlert, X, } from "lucide-react";
import { Children, cloneElement, useEffect, useRef, useState } from "react";
import { Icon, IconButton } from "./core.js";
import { useObsinaI18n } from "./i18n.js";
import { cx, joinIds, toneProperties, useStableId } from "./utils.js";
const feedbackIcons = {
    accent: Sparkles,
    success: CircleCheck,
    warning: TriangleAlert,
    danger: OctagonAlert,
    info: Info,
};
export function Callout({ title, tone = "info", icon, onDismiss, announce = false, children, className, style, ...props }) {
    const { messages } = useObsinaI18n();
    return (_jsxs("div", { className: cx("obsina-callout", className), role: announce ? (tone === "danger" ? "alert" : "status") : undefined, style: { ...toneProperties(tone), ...style }, ...props, children: [_jsx("span", { "aria-hidden": "true", className: "obsina-callout__icon", children: _jsx(Icon, { icon: icon ?? feedbackIcons[tone] }) }), _jsxs("div", { className: "obsina-callout__content", children: [title && _jsx("div", { className: "obsina-callout__title", children: title }), children && _jsx("div", { className: "obsina-callout__body", children: children })] }), onDismiss && (_jsx(IconButton, { "aria-label": messages.dismiss, icon: X, onClick: onDismiss, size: "sm" }))] }));
}
export function Tooltip({ children, label, placement = "top", className, }) {
    const id = useStableId(undefined, "obsina-tooltip");
    const [open, setOpen] = useState(false);
    const trigger = Children.only(children);
    const describedBy = joinIds(trigger.props["aria-describedby"], id);
    return (_jsxs("span", { className: cx("obsina-tooltip-anchor", className), onBlurCapture: () => setOpen(false), onFocusCapture: () => setOpen(true), onKeyDown: (event) => {
            if (event.key === "Escape")
                setOpen(false);
        }, onMouseEnter: () => setOpen(true), onMouseLeave: () => setOpen(false), children: [cloneElement(trigger, { "aria-describedby": describedBy }), _jsx("span", { className: "obsina-tooltip", "data-open": open || undefined, "data-placement": placement, id: id, role: "tooltip", children: label })] }));
}
export function Dialog({ open, title, description, footer, size = "md", onOpenChange, closeOnBackdrop = true, children, className, ...props }) {
    const { messages } = useObsinaI18n();
    const ref = useRef(null);
    const titleId = useStableId(undefined, "obsina-dialog-title");
    const descriptionId = useStableId(undefined, "obsina-dialog-description");
    useEffect(() => {
        const dialog = ref.current;
        if (!dialog)
            return;
        if (open && !dialog.open) {
            if (typeof dialog.showModal === "function")
                dialog.showModal();
            else
                dialog.setAttribute("open", "");
        }
        else if (!open && dialog.open) {
            if (typeof dialog.close === "function")
                dialog.close();
            else
                dialog.removeAttribute("open");
        }
    }, [open]);
    const dialogStyle = {
        "--obsina-dialog-size": size === "sm" ? "28rem" : size === "lg" ? "52rem" : "36rem",
    };
    return (_jsxs("dialog", { ...props, "aria-describedby": description ? descriptionId : undefined, "aria-labelledby": titleId, "aria-modal": "true", className: cx("obsina-dialog", className), onCancel: (event) => {
            event.preventDefault();
            onOpenChange(false);
        }, onClick: (event) => {
            if (closeOnBackdrop && event.target === event.currentTarget)
                onOpenChange(false);
        }, ref: ref, style: dialogStyle, children: [_jsxs("header", { className: "obsina-dialog__header", children: [_jsxs("div", { className: "obsina-dialog__heading", children: [_jsx("h2", { className: "obsina-dialog__title", id: titleId, children: title }), description && (_jsx("p", { className: "obsina-dialog__description", id: descriptionId, children: description }))] }), _jsx(IconButton, { "aria-label": messages.close, icon: X, onClick: () => onOpenChange(false), size: "sm" })] }), _jsx("div", { className: "obsina-dialog__body", children: children }), footer && _jsx("footer", { className: "obsina-dialog__footer", children: footer })] }));
}
export function Menu({ items, label, trigger, align = "end", className, ...props }) {
    const { messages } = useObsinaI18n();
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const surfaceRef = useRef(null);
    const menuId = useStableId(undefined, "obsina-menu");
    const accessibleLabel = label ?? messages.menu;
    useEffect(() => {
        if (!open)
            return;
        surfaceRef.current
            ?.querySelector("[role='menuitem']:not(:disabled)")
            ?.focus();
        const close = (event) => {
            if (!rootRef.current?.contains(event.target))
                setOpen(false);
        };
        document.addEventListener("pointerdown", close);
        return () => document.removeEventListener("pointerdown", close);
    }, [open]);
    const handleKeys = (event) => {
        const controls = [
            ...(surfaceRef.current?.querySelectorAll("[role='menuitem']:not(:disabled)") ?? []),
        ];
        const current = controls.indexOf(document.activeElement);
        if (event.key === "Escape") {
            setOpen(false);
            triggerRef.current?.focus();
        }
        else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            const delta = event.key === "ArrowDown" ? 1 : -1;
            controls[(current + delta + controls.length) % controls.length]?.focus();
        }
        else if (event.key === "Home")
            controls[0]?.focus();
        else if (event.key === "End")
            controls.at(-1)?.focus();
    };
    return (_jsxs("div", { className: cx("obsina-menu", className), ref: rootRef, ...props, children: [_jsx("button", { "aria-controls": menuId, "aria-expanded": open, "aria-haspopup": "menu", "aria-label": accessibleLabel, className: "obsina-icon-button obsina-icon-button--ghost obsina-icon-button--md", onClick: () => setOpen((value) => !value), ref: triggerRef, type: "button", children: trigger ?? _jsx(Icon, { icon: MoreHorizontal }) }), open && (_jsx("div", { "aria-label": accessibleLabel, className: "obsina-menu__surface", id: menuId, onKeyDown: handleKeys, ref: surfaceRef, role: "menu", style: align === "start"
                    ? { insetInlineStart: 0, insetInlineEnd: "auto" }
                    : undefined, children: items.map((item) => item.type === "separator" ? (_jsx("div", { className: "obsina-menu__separator", role: "separator" }, item.id)) : (_jsxs("button", { className: "obsina-menu__item", "data-danger": item.danger ? true : undefined, disabled: item.disabled, onClick: () => {
                        item.onSelect();
                        setOpen(false);
                        triggerRef.current?.focus();
                    }, role: "menuitem", type: "button", children: [item.icon && _jsx(Icon, { icon: item.icon, size: 16 }), _jsx("span", { children: item.label })] }, item.id))) }))] }));
}
export function Toast({ toast, onDismiss, className, style, ...props }) {
    const { messages } = useObsinaI18n();
    const tone = toast.tone ?? "info";
    return (_jsxs("div", { className: cx("obsina-toast", className), role: tone === "danger" ? "alert" : "status", style: { ...toneProperties(tone), ...style }, ...props, children: [_jsx("span", { "aria-hidden": "true", className: "obsina-callout__icon", children: _jsx(Icon, { icon: feedbackIcons[tone] }) }), _jsxs("div", { className: "obsina-toast__content", children: [_jsx("div", { className: "obsina-toast__title", children: toast.title }), toast.message && (_jsx("div", { className: "obsina-toast__message", children: toast.message }))] }), onDismiss && (_jsx(IconButton, { "aria-label": messages.dismiss, icon: X, onClick: () => onDismiss(toast.id), size: "sm" }))] }));
}
export function ToastRegion({ toasts, onDismiss, label, className, ...props }) {
    const { messages } = useObsinaI18n();
    return (_jsx("div", { "aria-label": label ?? messages.toastRegion, className: cx("obsina-toast-region", className), role: "region", ...props, children: toasts.map((toast) => (_jsx(Toast, { onDismiss: onDismiss, toast: toast }, toast.id))) }));
}
//# sourceMappingURL=feedback.js.map